import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ScannerAuth from './components/ScannerAuth';
import ScannerCamera from './components/ScannerCamera';
import ScanResult from './components/ScanResult';
import ScanHistory from './components/ScanHistory';
import ManualEntry from './components/ManualEntry';
import OfflineQueue from './components/OfflineQueue';

const EventScanner = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scannerInfo, setScannerInfo] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queuedScans, setQueuedScans] = useState([]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load queued scans from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('tixful_queued_scans');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setQueuedScans(parsed?.map(scan => ({
          ...scan,
          timestamp: new Date(scan.timestamp)
        })));
      } catch (error) {
        console.error('Error loading queued scans:', error);
      }
    }
  }, []);

  // Save queued scans to localStorage
  useEffect(() => {
    localStorage.setItem('tixful_queued_scans', JSON.stringify(queuedScans));
  }, [queuedScans]);

  const handleAuthenticate = (authData) => {
    setIsLoading(true);
    setTimeout(() => {
      setScannerInfo(authData);
      setIsAuthenticated(true);
      setIsLoading(false);
    }, 1000);
  };

  const handleScanResult = (result) => {
    setCurrentResult(result);
    
    // Add to history
    setScanHistory(prev => [result, ...prev]?.slice(0, 50)); // Keep last 50 scans
    
    // If offline, add to queue
    if (!isOnline) {
      setQueuedScans(prev => [...prev, result]);
    }
    
    // Stop scanning after successful scan
    setIsScanning(false);
  };

  const handleManualScan = (result) => {
    handleScanResult(result);
  };

  const handleManualOverride = (originalResult) => {
    const overrideResult = {
      ...originalResult,
      isValid: true,
      isOverride: true,
      timestamp: new Date()
    };
    handleScanResult(overrideResult);
  };

  const handleToggleScanning = () => {
    setIsScanning(!isScanning);
    if (currentResult) {
      setCurrentResult(null);
    }
  };

  const handleClearResult = () => {
    setCurrentResult(null);
  };

  const handleClearHistory = () => {
    setScanHistory([]);
  };

  const handleSyncQueue = async () => {
    // Simulate sync process
    return new Promise((resolve) => {
      setTimeout(() => {
        setQueuedScans([]);
        resolve();
      }, 2000);
    });
  };

  const handleClearQueue = () => {
    setQueuedScans([]);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setScannerInfo(null);
    setIsScanning(false);
    setCurrentResult(null);
    setScanHistory([]);
    setQueuedScans([]);
    localStorage.removeItem('tixful_queued_scans');
  };

  const handleBackToDashboard = () => {
    navigate('/event-manager-dashboard');
  };

  // Show authentication screen if not authenticated
  if (!isAuthenticated) {
    return (
      <ScannerAuth 
        onAuthenticate={handleAuthenticate}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-elevation-1 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              onClick={handleBackToDashboard}
              className="text-muted-foreground hover:text-foreground"
            />
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="QrCode" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Event Scanner</h1>
                <p className="text-xs text-muted-foreground">
                  {scannerInfo?.eventName || 'Unknown Event'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-foreground">
                {scannerInfo?.scannerName || 'Scanner'}
              </p>
              <p className="text-xs text-muted-foreground">
                Code: {scannerInfo?.code}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="LogOut"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 p-4 space-y-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Camera and Controls */}
            <div className="lg:col-span-2 space-y-4">
              {/* Scanner Camera */}
              <div className="aspect-video">
                <ScannerCamera
                  isScanning={isScanning}
                  onScanResult={handleScanResult}
                  onToggleScanning={handleToggleScanning}
                  scannerCode={scannerInfo?.code}
                  isAuthenticated={isAuthenticated}
                />
              </div>

              {/* Scan Result */}
              {currentResult && (
                <ScanResult
                  result={currentResult}
                  onClear={handleClearResult}
                  onManualOverride={handleManualOverride}
                />
              )}

              {/* Manual Entry */}
              <ManualEntry
                onManualScan={handleManualScan}
                isProcessing={isLoading}
              />
            </div>

            {/* Right Column - History and Status */}
            <div className="space-y-4">
              {/* Offline Queue */}
              <OfflineQueue
                isOnline={isOnline}
                queuedScans={queuedScans}
                onSyncQueue={handleSyncQueue}
                onClearQueue={handleClearQueue}
              />

              {/* Scan History */}
              <ScanHistory
                history={scanHistory}
                onClearHistory={handleClearHistory}
              />

              {/* Scanner Stats */}
              <div className="bg-card rounded-lg shadow-elevation-2 p-4">
                <h3 className="text-sm font-medium text-foreground mb-3">Session Stats</h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-success">{scanHistory?.filter(s => s?.isValid)?.length}</p>
                    <p className="text-xs text-muted-foreground">Valid Scans</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-error">{scanHistory?.filter(s => !s?.isValid)?.length}</p>
                    <p className="text-xs text-muted-foreground">Invalid Scans</p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Session started:</span>
                    <span>{new Date()?.toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-error'}`}></div>
                <span>{isOnline ? 'Online' : 'Offline'}</span>
              </div>
              <span>•</span>
              <span>Venue: {scannerInfo?.venue || 'Unknown Venue'}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Emergency Support: (555) 123-4567
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventScanner;